import { Link } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import SectionEyebrow from '../components/SectionEyebrow';

export default function Vision() {
  return (
    <div>
      {/* HERO */}
      <section className="pt-48 pb-24 bg-forest-900 text-cream relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" aria-hidden>
          <svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1440 800">
            <defs>
              <radialGradient id="vg" cx="50%" cy="50%" r="60%">
                <stop offset="0%" stopColor="#90b193" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#1f2f24" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect width="1440" height="800" fill="url(#vg)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <Breadcrumb current="The Vision" />

          <div className="mt-16 max-w-4xl">
            <div className="w-20 h-20 mb-10">
              <img src="/favicon2.svg" alt="Costa Nativa" className="w-full h-full object-contain brightness-0 invert" />
            </div>

            <h1 className="font-display text-5xl lg:text-8xl leading-[0.95]">
              The forest came first.
              <br />
              <span className="italic text-sand-300">We built around it.</span>
            </h1>
            <p className="mt-10 max-w-2xl text-cream/80 text-lg lg:text-xl leading-relaxed">
              A community is a verb, not a development. Here is what we
              believe, and what we are committed to.
            </p>
          </div>
        </div>
      </section>

      {/* DIFFERENT QUESTION */}
      <section className="py-24 lg:py-32 bg-cream">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="eyebrow text-clay-600 mb-6">A Different Question</div>
          <p className="font-display text-3xl lg:text-4xl text-forest-900 leading-snug">
            Most coastal developments ask:{' '}
            <span className="italic text-clay-500">
              what can we put on this land?
            </span>
            <br />
            We started with a quieter one.
          </p>

          <div className="mt-12 space-y-6 text-forest-900/80 text-lg leading-relaxed">
            <p>
              What does this land already want to do? What was already growing
              here before any of us arrived? What lives in the canopy, drinks
              from the river, walks the trails at night that no one mapped?
              What stays, and what gets out of the way?
            </p>
            <p>
              Those questions led us to a simple commitment. The forest does
              not get cleared for the village. The village gets built around
              the forest. Every road bends. Every home steps back. Every
              neighborhood defers to the river that runs through all of them.
            </p>
            <p className="font-display italic text-2xl text-forest-900">
              This is the document of what we mean by that.
            </p>
          </div>
        </div>
      </section>

      {/* THREE PRINCIPLES */}
      <section className="py-24 lg:py-32 bg-forest-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-20">
            <SectionEyebrow>The Three Principles</SectionEyebrow>
            <h2 className="mt-6 font-display text-5xl lg:text-6xl leading-tight text-forest-900">
              What we believe.{' '}
              <span className="italic text-clay-500">
                And how it shapes everything.
              </span>
            </h2>
          </div>

          <div className="space-y-16 lg:space-y-24">
            <Principle
              number="01"
              eyebrow="The River Decides"
              title="The river is the spine. Everything else defers to it."
              body={[
                'The Río San Clemente runs the length of the property: from the foothills of the Talamanca range to where it meets the Caribbean. It is not a feature. It is the organizing principle of the entire community.',
                'Fifty to one hundred meters of protected forest corridor flank it on both sides, in perpetuity. No structure crosses that line. No road parallels it inside the buffer. No private dock claims it. The river belongs to itself, and to the wildlife that depends on it. The community gathers around it, walks beside it, swims in its quiet pools, and lets it be.',
              ]}
            />
            <Principle
              number="02"
              eyebrow="The Forest Stays"
              title="We do not clear-cut. We tuck in."
              body={[
                'More than fifty-five percent of the property is permanent conservation, held in trust, not pending zoning approval. The remaining forty-five percent is where homes, paths, and shared spaces sit, threaded into the canopy rather than punched through it.',
                'Architecture follows the trees, not the other way around. Roads bend around root systems. Building footprints get smaller, not larger, where the canopy is densest. Every native specimen tree on the property is mapped and protected before a single foundation is poured. The forest came first. It will be here long after any of us.',
              ]}
            />
            <Principle
              number="03"
              eyebrow="The Place Has People"
              title="We arrived here. We did not invent it."
              body={[
                'The Caribbean coast of Costa Rica has a culture: Afro-Caribbean, Bribri indigenous, fishing village, cacao farmer, soundtrack of dub and calypso. None of that is decoration for a marketing brochure. It is the actual place.',
                'We hire locally. We source locally. We partner with Bribri communities on cacao stewardship. We invite, and pay, local cooks, musicians, naturalists, and artisans to be part of community life on the land. A regenerative project that displaces the people who already live nearby is not regenerative. It is just nicer extraction. We refuse that trade.',
              ]}
            />
          </div>
        </div>
      </section>

      {/* COMMITMENTS */}
      <section className="py-24 lg:py-32 bg-forest-900 text-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <SectionEyebrow>
              <span className="text-sand-300">The Commitments</span>
            </SectionEyebrow>
            <h2 className="mt-6 font-display text-5xl lg:text-6xl leading-tight">
              What we will{' '}
              <span className="italic text-sand-300">
                measure ourselves
              </span>{' '}
              against.
            </h2>
            <p className="mt-6 text-cream/70 leading-relaxed">
              Stated up front. Audited annually. Published openly.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-cream/10">
            <Commit
              big="55%+"
              label="Permanent Conservation"
              body="Held in conservation trust. Not subject to future rezoning or rule change."
            />
            <Commit
              big="Net-zero"
              label="Operational Energy"
              body="Solar, micro-hydro, and grid offset across all community amenities."
            />
            <Commit
              big="100%"
              label="Biological Wastewater"
              body="Constructed wetlands and reed beds. Nothing untreated touches the river."
            />
            <Commit
              big="0"
              label="Non-native Landscaping"
              body="Indigenous plant species only, across every common area and design covenant."
            />
          </div>

          <div className="mt-16 max-w-4xl">
            <p className="font-display text-3xl lg:text-4xl text-sand-300 italic leading-snug">
              A house can last a century.{' '}
              <span className="text-cream not-italic">
                The forest around it can last a thousand years.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* PROMISE */}
      <section className="py-24 lg:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionEyebrow>The Promise to Owners</SectionEyebrow>
              <h2 className="mt-6 font-display text-5xl lg:text-6xl leading-tight text-forest-900">
                What you are{' '}
                <span className="italic text-clay-500">actually buying</span>{' '}
                when you buy in.
              </h2>
              <p className="mt-6 text-forest-900/80 leading-relaxed">
                You are not buying a lot. You are joining a covenant, one that
                protects the value of what you have bought by protecting the
                place around it. The community design rules, the conservation
                easements, the architectural review, the materials standards:
                these are the things that keep Nativa from becoming just
                another coastal development in twenty years.
              </p>
              <p className="mt-6 text-forest-900 font-display text-2xl italic">
                That is how we stay rare. And how your investment holds.
              </p>
            </div>

            <div className="lg:col-span-7 space-y-px bg-forest-900/10">
              <Promise
                title="Design covenants"
                body="Every home reviewed for materials, scale, and forest impact."
              />
              <Promise
                title="Conservation easement"
                body="The 55%+ protected land is recorded on title. Permanent."
              />
              <Promise
                title="HOA stewardship fund"
                body="A portion of every fee goes to land restoration and trail maintenance."
              />
              <Promise
                title="Annual transparency report"
                body="Energy, water, waste, biodiversity. Published every year. No spin."
              />
              <Promise
                title="Owner voice in governance"
                body="The community evolves with its members. Not for them."
              />
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO LIST */}
      <section className="py-32 bg-forest-50 text-forest-900">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <div className="w-16 h-16 mx-auto mb-10 rounded-full bg-forest-700 flex items-center justify-center">
            <span className="font-display text-cream text-3xl leading-none">
              N
            </span>
          </div>
          <ul className="font-display text-3xl lg:text-4xl leading-[1.4] space-y-4">
            <li>
              Live <span className="italic text-clay-500">lightly</span> on the
              land.
            </li>
            <li>
              Build <span className="italic text-clay-500">quietly</span> in
              the forest.
            </li>
            <li>
              Listen <span className="italic text-clay-500">closely</span> to
              the river.
            </li>
            <li>
              Stay <span className="italic text-clay-500">generously</span>{' '}
              with the people.
            </li>
            <li>
              Leave <span className="italic text-clay-500">more</span> than you
              found.
            </li>
          </ul>
          <div className="mt-12 eyebrow text-forest-700/70">: The Nativa Way</div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-cream">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <SectionEyebrow className="justify-center">
            From philosophy to plan
          </SectionEyebrow>
          <h2 className="mt-6 font-display text-4xl lg:text-6xl leading-tight text-forest-900">
            The vision becomes a place.{' '}
            <span className="italic text-clay-500">See how it gets built.</span>
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-forest-900/70 leading-relaxed">
            Eight gated neighborhoods. Four lot tiers. One river spine. Phase 1
            inventory open now.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="/lots"
              className="px-7 py-4 bg-forest-700 text-cream font-medium tracking-wide hover:bg-forest-900 transition-colors"
            >
              Explore the Lots →
            </Link>
            <Link
              to="/land"
              className="px-7 py-4 border border-forest-900/30 text-forest-900 font-medium tracking-wide hover:bg-forest-900 hover:text-cream transition-colors"
            >
              Back to The Land
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Principle({
  number,
  eyebrow,
  title,
  body,
}: {
  number: string;
  eyebrow: string;
  title: string;
  body: string[];
}) {
  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <div className="font-display text-7xl text-clay-500 leading-none">
          {number}
        </div>
        <div className="mt-3 eyebrow text-clay-600">{eyebrow}</div>
      </div>
      <div className="lg:col-span-8">
        <h3 className="font-display text-3xl lg:text-4xl text-forest-900 leading-tight">
          {title.split('. ').map((part, i) => (
            <span key={i}>
              {i > 0 && '. '}
              {i === 1 ? <em className="text-clay-500">{part}</em> : part}
            </span>
          ))}
        </h3>
        <div className="mt-6 space-y-4 text-forest-900/80 leading-relaxed">
          {body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </div>
    </div>
  );
}

function Commit({ big, label, body }: { big: string; label: string; body: string }) {
  return (
    <div className="p-8 lg:p-10 bg-forest-900">
      <div className="font-display text-5xl lg:text-6xl text-sand-300">
        {big}
      </div>
      <div className="mt-3 eyebrow text-cream/80">{label}</div>
      <p className="mt-5 text-sm text-cream/70 leading-relaxed">{body}</p>
    </div>
  );
}

function Promise({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-cream p-6 flex items-start gap-4">
      <div className="text-clay-500 font-display text-xl leading-none mt-0.5">
        ◆
      </div>
      <div>
        <div className="font-display text-xl text-forest-900">{title}</div>
        <p className="mt-1 text-forest-900/70 text-sm leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
